import { RenderAirCardProps, RenderAirSearchErrorCardProps } from "entities/shared/airCard.types";
import { ShipmentType } from "entities/shared/shipmentCard.types";
import { ValueOf } from "shared/lib/typeCheck.lib";
import { useTranslation } from "shared/localization/localization";

import { AirSearchCardModel } from "../model/model";

type AirSearchCardProps = {
  shipmentType: ValueOf<typeof ShipmentType>;
  data: AirSearchCardModel;
  isAuthenticated: boolean;
  className?: string;
  openCard: (data: AirSearchCardModel) => void;
  follow: (card: AirSearchCardModel) => void;
  deleteCardHandler: (card: AirSearchCardModel) => void;
  isFollowButtonVisible: boolean;
  isFollowLoading: boolean;
  renderCard: (props: RenderAirCardProps) => JSX.Element;
  renderErrorCard: (props: RenderAirSearchErrorCardProps) => JSX.Element;
};

export const AirSearchCard: RFC<AirSearchCardProps> = ({
  shipmentType,
  data,
  isAuthenticated,
  follow,
  deleteCardHandler,
  isFollowButtonVisible,
  isFollowLoading,
  openCard,
  renderCard,
  className,
  renderErrorCard,
}) => {
  const { t } = useTranslation();

  const onFollowHandler = () => {
    if (isFollowLoading) return;
    follow(data);
  };

  const onDeleteHandler = () => deleteCardHandler(data);

  const onOpenHandler = () => {
    if (isFollowLoading) return;
    openCard(data);
  };

  if (!data.isSuccess) {
    const errorText = data.searchErrorText(t("Something went wrong. Please try again later")) ?? t("Unexpected error");
    return renderErrorCard({
      text: `${errorText}.`,
      status: "error",
      reference: data.reference,
      onDelete: onDeleteHandler,
      onFollow: onFollowHandler,
      className: className,
    });
  }

  return renderCard({
    shipmentType,
    className: className,
    ata: data.ata,
    atd: data.atd,
    eta: data.eta,
    etd: data.etd,
    pieces: data.pieces,
    reference: data.reference,
    status: data.status,
    directionFrom: data.directionFrom,
    directionTo: data.directionTo,
    progressLine: { status: "location-data", percent: data.progress, rotation: "horizontal" },
    onOpen: onOpenHandler,
    onDelete: onDeleteHandler,
    onFollow: isFollowButtonVisible && isAuthenticated ? onFollowHandler : undefined,
    isFollowLoading: isFollowLoading,
    scac: data.iataCode || null,
    carrierName: data.carrierName?.name ?? data.airlineName ?? null,
  });
};
